import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  Clock,
  Eye,
  Filter,
  HeartPulse,
  HelpCircle,
  Key,
  Pencil,
  Play,
  RefreshCw,
  Shield,
  XCircle,
} from "lucide-react";
import type { Hook, HookPoint, Plugin } from "../../types";
import { getBindingDisplayInfo, getHealthStatusInfo } from "../../utils";

type ViewPluginModalProps = {
  plugin: Plugin | null;
  hooks?: Hook[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const HealthIcon = ({ status }: { status: string }) => {
  const info = getHealthStatusInfo(status);
  const iconClass = cn("size-4", info.colorClass);

  switch (info.icon) {
    case "check":
      return <Check className={iconClass} />;
    case "warning":
      return <AlertTriangle className={iconClass} />;
    case "error":
      return <XCircle className={iconClass} />;
    default:
      return <HelpCircle className={iconClass} />;
  }
};

const AUTH_TYPE_LABELS: Record<string, string> = {
  bearer: "Bearer Token",
  mtls: "mTLS",
  none: "None",
};

const HOOK_POINT_CONFIG: Record<
  HookPoint,
  { label: string; icon: typeof Eye; description: string }
> = {
  "tool.access": {
    label: "Access Check",
    icon: Eye,
    description: "Controls which users can see and use this tool",
  },
  "tool.pre": {
    label: "Pre tool execution",
    icon: Play,
    description: "Validates inputs and checks permissions before execution",
  },
  "tool.post": {
    label: "Post tool execution",
    icon: Filter,
    description: "Filters or transforms outputs after execution",
  },
};

const SectionHeader = ({
  icon: Icon,
  title,
}: {
  icon: typeof Eye;
  title: string;
}) => (
  <div className="mb-5 flex items-center gap-3">
    <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
      <Icon className="size-5 text-brand-text" />
    </div>
    <h4 className="font-semibold text-lg">{title}</h4>
  </div>
);

const ConfigRow = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="flex items-start justify-between gap-8 py-3">
    <span className="shrink-0 font-medium text-muted-foreground text-sm">
      {label}
    </span>
    <span
      className={cn(
        "text-right text-sm",
        mono && "rounded-md bg-muted px-3 py-1.5 font-mono"
      )}
    >
      {value}
    </span>
  </div>
);

const FAILURE_MODE_LABELS: Record<string, { label: string; variant: string }> =
  {
    fail_closed: { label: "Fail Closed", variant: "destructive" },
    fail_open: { label: "Fail Open", variant: "secondary" },
  };

const PHASE_LABELS: Record<string, string> = {
  before: "Before",
  after: "After",
};

const formatTimeout = (ms: number) => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)}s`;
  }
  return `${ms}ms`;
};

const formatCacheTtl = (ms: number) => {
  if (ms >= 86_400_000) {
    return `${(ms / 86_400_000).toFixed(1)}d`;
  }
  if (ms >= 3_600_000) {
    return `${(ms / 3_600_000).toFixed(1)}h`;
  }
  if (ms >= 60_000) {
    return `${(ms / 60_000).toFixed(0)}m`;
  }
  return `${(ms / 1000).toFixed(0)}s`;
};

const EndpointCard = ({
  hookPoint,
  url,
  hook,
}: {
  hookPoint: HookPoint;
  url: string;
  hook?: Hook;
}) => {
  const config = HOOK_POINT_CONFIG[hookPoint];
  const Icon = config.icon;
  const failureInfo = FAILURE_MODE_LABELS[hook?.failure_mode ?? ""] ?? {
    label: "Unknown",
    variant: "outline",
  };

  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm transition-colors hover:bg-accent/5">
      <div className="flex items-start gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <Icon className="size-6 text-brand-text" />
        </div>
        <div className="flex-1">
          <span className="font-semibold text-base">{config.label}</span>
          <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
            {config.description}
          </p>
        </div>
      </div>

      {/* Webhook URL */}
      <div className="mt-5">
        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
          Webhook URL
        </span>
        <p className="mt-2 break-all rounded-lg bg-muted px-4 py-3 font-mono text-sm">
          {url}
        </p>
      </div>

      {/* Hook configuration details */}
      {!!hook && (
        <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-muted/30 p-4 sm:grid-cols-3">
          <div>
            <span className="block font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              Priority
            </span>
            <span className="mt-1 block font-medium text-sm">
              {hook.priority}
            </span>
          </div>
          <div>
            <span className="block font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              Phase
            </span>
            <span className="mt-1 block font-medium text-sm">
              {PHASE_LABELS[hook.phase] ?? hook.phase}
            </span>
          </div>
          <div>
            <span className="block font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              Failure Mode
            </span>
            <Badge
              className="mt-1 text-xs"
              variant={
                // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                failureInfo.variant as "destructive" | "secondary" | "outline"
              }
            >
              {failureInfo.label}
            </Badge>
          </div>
          <div>
            <span className="block font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              Timeout
            </span>
            <span className="mt-1 block font-mono text-sm">
              {formatTimeout(hook.timeout_ms)}
            </span>
          </div>
          {hook.cache_enabled !== null && (
            <div>
              <span className="block font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
                Cache
              </span>
              <span className="mt-1 block text-sm">
                {hook.cache_enabled ? (
                  <Badge
                    className="bg-green-500/10 text-green-600 text-xs"
                    variant="outline"
                  >
                    {`Enabled${hook.cache_ttl_ms ? ` (${formatCacheTtl(hook.cache_ttl_ms)})` : ""}`}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">Disabled</span>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ConfiguredBadge = () => (
  <span className="flex items-center gap-2">
    <Check className="size-4 text-green-500" />
    Configured
  </span>
);

const NotConfiguredText = () => (
  <span className="text-muted-foreground">Not configured</span>
);

const BearerAuthSection = ({ token }: { token?: { exists?: boolean } }) => (
  <ConfigRow
    label="Token"
    value={
      token?.exists ? (
        <span className="flex items-center gap-2">
          <Check className="size-4 text-green-500" />
          <span className="font-mono text-sm">••••••••</span>
        </span>
      ) : (
        <NotConfiguredText />
      )
    }
  />
);

const MtlsAuthSection = ({
  auth,
}: {
  auth?: {
    client_cert_configured?: boolean;
    client_key_configured?: boolean;
    ca_cert?: string;
  };
}) => (
  <>
    <ConfigRow
      label="Client Certificate"
      value={
        auth?.client_cert_configured ? (
          <ConfiguredBadge />
        ) : (
          <NotConfiguredText />
        )
      }
    />
    <ConfigRow
      label="Client Key"
      value={
        auth?.client_key_configured ? (
          <ConfiguredBadge />
        ) : (
          <NotConfiguredText />
        )
      }
    />
    {!!auth?.ca_cert && (
      <ConfigRow label="CA Certificate" value={<ConfiguredBadge />} />
    )}
  </>
);

const RetryConfigSection = ({
  retry,
}: {
  retry: NonNullable<Plugin["retry"]>;
}) => (
  <>
    <ConfigRow label="Max Attempts" value={retry.max_attempts} />
    <ConfigRow
      label="Backoff Strategy"
      value={retry.backoff === "exponential" ? "Exponential" : "Fixed"}
    />
    <ConfigRow label="Base Delay" value={`${retry.base_delay_ms}ms`} />
    {retry.retry_on.length > 0 && (
      <ConfigRow
        label="Retry On"
        value={
          <div className="flex flex-wrap justify-end gap-1.5">
            {retry.retry_on.map((condition) => (
              <Badge
                className="px-2 py-0.5 text-xs"
                key={condition}
                variant="outline"
              >
                {condition}
              </Badge>
            ))}
          </div>
        }
      />
    )}
  </>
);

const EndpointsList = ({
  webhookConfig,
  hooks,
}: {
  webhookConfig?: Plugin["webhook_config"];
  hooks?: Hook[];
}) => {
  if (!webhookConfig) {
    return (
      <div className="rounded-xl border border-dashed bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground">No endpoints configured</p>
      </div>
    );
  }

  const { endpoints } = webhookConfig;
  const hasEndpoints = endpoints.access || endpoints.pre || endpoints.post;

  if (!hasEndpoints) {
    return (
      <div className="rounded-xl border border-dashed bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground">No endpoints configured</p>
      </div>
    );
  }

  const hookByPoint = (point: HookPoint) =>
    hooks?.find((h) => h.hook_point === point);

  return (
    <div className="flex flex-col gap-4">
      {!!endpoints.access && (
        <EndpointCard
          hook={hookByPoint("tool.access")}
          hookPoint="tool.access"
          url={endpoints.access.url}
        />
      )}
      {!!endpoints.pre && (
        <EndpointCard
          hook={hookByPoint("tool.pre")}
          hookPoint="tool.pre"
          url={endpoints.pre.url}
        />
      )}
      {!!endpoints.post && (
        <EndpointCard
          hook={hookByPoint("tool.post")}
          hookPoint="tool.post"
          url={endpoints.post.url}
        />
      )}
    </div>
  );
};

export const ViewPluginModal = ({
  plugin,
  hooks,
  open,
  onOpenChange,
}: ViewPluginModalProps) => {
  if (!plugin) {
    return null;
  }

  const healthInfo = getHealthStatusInfo(plugin.health_status);
  const bindingInfo = getBindingDisplayInfo(
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    plugin.binding.type as "tenant" | "project"
  );
  const hasRetryConfig = plugin.retry?.enabled;
  const hasHealthCheck = !!plugin.webhook_config?.health_check_path;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="!max-w-[1400px] sm:!max-w-[1400px] max-h-[90vh] w-[95vw] overflow-y-auto p-8">
        <DialogHeader className="pb-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <DialogTitle className="font-bold text-3xl">
                {plugin.name}
              </DialogTitle>
              {!!plugin.description && (
                <DialogDescription className="mt-3 max-w-3xl text-base leading-relaxed">
                  {plugin.description}
                </DialogDescription>
              )}
            </div>
            <Button
              render={
                <Link
                  from="/orgs/$organizationId/projects/$projectId/contextual-access/$pluginId"
                  onClick={() => onOpenChange(false)}
                  params={{ pluginId: plugin.id }}
                  to="."
                />
              }
              size="default"
              type="button"
            >
              <Pencil className="mr-2 size-4" />
              Edit Configuration
            </Button>
          </div>

          {/* Status Badges */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Badge
              className={cn("px-3 py-1 text-sm", bindingInfo.colorClass)}
              variant="outline"
            >
              {bindingInfo.label}
            </Badge>
            <Badge
              className="px-3 py-1 text-sm"
              variant={plugin.status === "active" ? "default" : "secondary"}
            >
              {plugin.status === "active" ? "Active" : "Inactive"}
            </Badge>
            <Badge className="gap-1.5 px-3 py-1 text-sm" variant="outline">
              <HealthIcon status={plugin.health_status} />
              {healthInfo.label}
            </Badge>
            <Badge className="px-3 py-1 text-sm" variant="outline">
              🔗 Webhook
            </Badge>
          </div>
        </DialogHeader>

        <Separator className="my-2" />

        <div className="grid gap-10 py-6 md:grid-cols-2">
          {/* Left Column */}
          <div className="flex flex-col gap-8">
            {/* Webhook Endpoints */}
            <div>
              <SectionHeader icon={Shield} title="Webhook Endpoints" />
              <EndpointsList
                hooks={hooks?.filter((h) => h.plugin_id === plugin.id)}
                webhookConfig={plugin.webhook_config}
              />
            </div>

            {/* Health Check */}
            {!!hasHealthCheck && (
              <div>
                <SectionHeader icon={HeartPulse} title="Health Check" />
                <div className="rounded-xl border bg-card p-6 shadow-sm">
                  <ConfigRow
                    label="Health Check Path"
                    mono
                    value={plugin.webhook_config?.health_check_path}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-8">
            {/* Authentication */}
            <div>
              <SectionHeader icon={Key} title="Authentication" />
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <ConfigRow
                  label="Type"
                  value={
                    <Badge className="px-2.5 py-0.5" variant="outline">
                      {AUTH_TYPE_LABELS[
                        plugin.webhook_config?.auth?.type ?? ""
                      ] ?? "None"}
                    </Badge>
                  }
                />
                {plugin.webhook_config?.auth?.type === "bearer" && (
                  <BearerAuthSection
                    token={plugin.webhook_config?.auth?.token}
                  />
                )}
                {plugin.webhook_config?.auth?.type === "mtls" && (
                  <MtlsAuthSection auth={plugin.webhook_config?.auth} />
                )}
              </div>
            </div>

            {/* Retry Configuration */}
            <div>
              <SectionHeader icon={RefreshCw} title="Retry Configuration" />
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <ConfigRow
                  label="Retries Enabled"
                  value={
                    hasRetryConfig ? (
                      <Badge
                        className="bg-green-500/10 px-2.5 py-0.5 text-green-600"
                        variant="outline"
                      >
                        Enabled
                      </Badge>
                    ) : (
                      <Badge className="px-2.5 py-0.5" variant="secondary">
                        Disabled
                      </Badge>
                    )
                  }
                />
                {!!(hasRetryConfig && plugin.retry) && (
                  <RetryConfigSection retry={plugin.retry} />
                )}
              </div>
            </div>

            {/* Metadata */}
            <div>
              <SectionHeader icon={Clock} title="Metadata" />
              <div className="rounded-xl border bg-card p-6 shadow-sm">
                <ConfigRow label="Plugin ID" mono value={plugin.id} />
                <ConfigRow label="Version" value={plugin.version} />
                <ConfigRow
                  label="Created"
                  value={
                    <span>
                      {new Date(plugin.created_at).toLocaleDateString()}{" "}
                      <span className="text-muted-foreground">
                        by {plugin.created_by}
                      </span>
                    </span>
                  }
                />
                <ConfigRow
                  label="Last Updated"
                  value={
                    <span>
                      {new Date(plugin.updated_at).toLocaleDateString()}{" "}
                      <span className="text-muted-foreground">
                        by {plugin.updated_by}
                      </span>
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-2" />

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            onClick={() => onOpenChange(false)}
            size="lg"
            type="button"
            variant="outline"
          >
            Close
          </Button>
          <Button
            render={
              <Link
                from="/orgs/$organizationId/projects/$projectId/contextual-access/$pluginId"
                onClick={() => onOpenChange(false)}
                params={{ pluginId: plugin.id }}
                to="."
              />
            }
            size="lg"
            type="button"
          >
            <Pencil className="mr-2 size-4" />
            Edit Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
